/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import TextField from '@TextField';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE from '../../Data/Maps/admiralQuestionIdToAutofillType';
import admiralQuestionsContainingPii from '../../Data/Maps/admiralQuestionsContainingPII';
import mappedQuestionIdIcons from '../../Data/Maps/mappedQuestionIdIcons';
import ValidationStatus from '../../Data/ValidationStatus';
import SagaStatus from '../../Data/SagaStatus';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import { getErrorMessage } from '../../Helpers/validation';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import {
  clearConstraintsValidations,
  setBrowserAutoFilledActive,
  setComponentValidationStatus,
  updateMaskedValue,
  updateValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import { mask } from '../../Utils/masking';
import QuestionText from '../QuestionText/QuestionText';
import {
  getComponentValidationStatus,
  hasPostcodeConstraint,
} from '../__helpers__/component.helpers';
import { getHtmlInputType } from './textfield.helpers';
import makeTextFieldInputStyling from './useTextFieldInputStyles';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

/**
 * Note there is no handling of phone number in this component as that is handled in PhoneNumberTextField
 */
const TextFieldInput = ({
  componentId,
  iFrameId,
  multiline,
  rows,
  text,
  subheading,
  value,
  formId,
}) => {
  const textfieldExperimentName = useGetExperimentName({
    componentId,
    componentName: 'TextField',
  });

  const { countryCode, isIos, isSafari, isAndroid } = useSelector(
    (store) => store.formConfig
  );
  const dispatch = useDispatch();
  const theme = useTheme();
  const styling = makeTextFieldInputStyling({ theme });
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const { componentIdToReceiveFocus } = useSelector(
    (store) => store.persistKeyboard
  );
  const { componentIdToSagaStatus } = useSelector(
    (store) => store.dataMappings
  );
  const isPostcodeComponent = hasPostcodeConstraint({ component });
  const { mappedQuestionId, displayQuestionTextInsideTextfield } =
    component.component;
  const {
    autoCompleteType,
    placeholder,
    isAutoFilledActive,
    maskPattern,
    maskedValue,
  } = component.input;

  const errorMessage = getErrorMessage(component.validation?.constraints);
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [attemptNavigate, setAttemptNavigate] = useState(false);

  let autoCompleteName;
  if (autoCompleteType === 'disabled') {
    autoCompleteName = 'off';
  } else if (
    Object.keys(ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE).includes(mappedQuestionId)
  ) {
    autoCompleteName = ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE[mappedQuestionId];
  } else {
    autoCompleteName = '';
  }

  const isPostcodeRegex = component.validation?.constraints?.postcodeRegex;

  const dispatchValidation = (currentValue) => {
    if (isPostcodeComponent && currentValue !== undefined && !isPostcodeRegex) {
      batch(() => {
        dispatch(
          validatePostcodeSaga({
            countryCode,
            value: currentValue,
            componentId,
            iFrameId,
            formId,
          })
        );
      });
    } else {
      batch(() => {
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });
    }
  };

  const handleChange = (params) => {
    batch(() => {
      if (isPostcodeComponent && maskPattern) {
        const newMaskedValue = mask({ value: params.value, maskPattern });
        dispatch(
          updateMaskedValue({ componentId, maskedValue: newMaskedValue })
        );
      }
      dispatch(updateValue({ componentId, value: params.value }));

      if (isAutoFilledActive) {
        dispatch(setBrowserAutoFilledActive({ componentId, value: false }));
      }
    });
  };

  const handleClick = () => {
    dispatch(
      clearConstraintsValidations({ componentId: component.component.id })
    );
  };

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      if (multiline === true) {
        event.stopPropagation();
        return;
      }

      const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
        component.component.page + 1,
        components,
        pages
      );

      if (
        (isIos || isAndroid) &&
        firstEditableComponentOnNextPage?.input?.type === 'textfield' &&
        admiralQuestionsContainingPii.includes(
          firstEditableComponentOnNextPage.component.mappedQuestionId
        )
      ) {
        dispatch(
          setComponentIdToReceiveFocus({
            componentIdToReceiveFocus:
              firstEditableComponentOnNextPage.component.id,
          })
        );
      }

      if (isPostcodeComponent && maskedValue) {
        dispatchValidation(mask({ value, maskPattern }));
      } else {
        dispatchValidation(value);
      }

      setAttemptNavigate(true);
    }
  };

  const handleKeyDown = (event) => {
    // Browser auto fill currently triggers key down events with the following key and code
    if (event.key === 'Unidentified' && event.code === undefined) {
      setIsAutoFilled(true);
    } else {
      setIsAutoFilled(false);
    }
  };

  const sagaStatus = componentIdToSagaStatus?.[componentId];

  useEffect(() => {
    const shouldPreventNavigation =
      sagaStatus === SagaStatus.PENDING && sagaStatus !== undefined;

    if (
      componentStatus === ValidationStatus.VALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
    if (
      componentStatus === ValidationStatus.INVALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
    }
  }, [componentStatus, attemptNavigate, componentId, sagaStatus, dispatch]);

  const inputRef = useRef(null);
  useAutoFocusFirstEditableComponent({ componentId, inputRef });

  useEffect(() => {
    if (inputRef.current && componentIdToReceiveFocus === componentId) {
      inputRef.current.focus({ preventScroll: true });
      dispatch(
        setComponentIdToReceiveFocus({ componentIdToReceiveFocus: null })
      );
    }
  }, [componentIdToReceiveFocus]);

  const handleBlur = () => {
    if (value === undefined || value === '') {
      return; // We don't want to validate empty fields until a navigation attempt is made
    }
    if (isPostcodeComponent && maskedValue) {
      dispatchValidation(mask({ value, maskPattern }));
    } else {
      dispatchValidation(value);
    }
  };

  useEffect(() => {
    if (isAutoFilled) {
      // We usually trigger a validation on blur of the textfield but if it is filled by autofill then we will not see this blur. Therefore we will instead trigger the validation immediately.
      if (isPostcodeComponent && maskedValue) {
        dispatchValidation(mask({ value, maskPattern }));
      } else {
        dispatchValidation(value);
      }
      setIsAutoFilled(false);
    }
  }, [value]);

  const displayedValue =
    isPostcodeComponent && maskPattern ? maskedValue : value || '';

  return (
    <Box
      onKeyDown={handleEnterPress}
      css={styling.box}
      data-testid="textFieldContainer"
    >
      {!displayQuestionTextInsideTextfield && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
          subheading={renderMustache({
            template: subheading,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <Box css={styling.textFieldBox}>
        <TextField
          autoComplete={autoCompleteName}
          errorMessage={errorMessage}
          iconType={mappedQuestionIdIcons[mappedQuestionId]}
          id={`textField-${componentId}`}
          isAnswered={isAnswered}
          isAutoFilledActive={isAutoFilledActive}
          isSafari={isSafari}
          label={!displayQuestionTextInsideTextfield ? '' : text}
          multiline={multiline}
          value={displayedValue}
          onChange={handleChange}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          type={getHtmlInputType(
            component.input.fieldSubtype,
            isIos,
            isPostcodeComponent
          )}
          fullWidth
          placeholder={placeholder}
          rows={rows}
          shrinkLabel
          inputProps={{
            enterKeyHint: 'go',
            maxLength:
              isPostcodeComponent && maskPattern
                ? maskPattern.length
                : undefined,
          }}
          inputRef={inputRef}
          experimentName={textfieldExperimentName}
        />
      </Box>
    </Box>
  );
};

export default TextFieldInput;
